<template>
  <ul
    ref="theMenu"
    :class="ulCssClass"
    :aria-labelledby="groupLabelId"
    :aria-label="groupLabelId ? null : groupLabel"
    @click.self="focusSubMenuItem"
  >
    <li
      v-for="(item, index) in items"
      :key="index"
      :class="liCssClass(item, index)"
      @click.self="focusSubMenuItem"
    >
      <a
        v-if="item.href && !item.text"
        :id="menuItemId(item, index)"
        :href="item.href"
        class="inline-block"
        role="link"
        :class="linkCssClass(item, index)"
        :tabindex="tabIndex(index)"
        :aria-label="item.cap"
        :data-label="item.cap"
        :data-menu-group="!!item.group"
        @click="selectMenu"
        @keydown.enter.stop="selectMenu"
        @keydown.down.prevent="focusNextItem(menuItemId(item, index))"
        @keydown.up.prevent="focusPreviousItem(menuItemId(item, index))"
        @keydown.right.prevent="
          focusNextItem(depth === 0 ? menuItemId(item, index) : null)
        "
        @keydown.left.prevent="
          focusPreviousItem(depth === 0 ? menuItemId(item, index) : null)
        "
        @keydown.esc="closeMenu($event, true)"
        @keydown.tab="closeMenu"
      >
        <span class="menu-link">{{ item.cap }}</span>
        <span v-if="item.relStatus === 'BETA'">
          <cat-pill
            v-if="item.relStatus"
            level="info"
            mild
            class="mlx"
            :value="item.relStatus"
          ></cat-pill>
        </span>
        <span v-else-if="item.relStatus === 'RELEASE'">
          <cat-pill
            v-if="item.relStatus"
            level="info"
            mild
            class="mlx"
            :value="item.relStatus"
          ></cat-pill>
        </span>
        <span v-else>
          <cat-pill
            v-if="item.relStatus"
            level="warn"
            mild
            class="mlx"
            :value="item.relStatus"
          ></cat-pill>
        </span>
      </a>
      <a
        v-else-if="item.id"
        :id="menuItemId(item, index)"
        class="inline-block"
        :class="menuLinkClass(item.css)"
        role="link"
        :tabindex="tabIndex(index)"
        :aria-label="item.cap"
        :data-label="item.cap"
        :data-menu-group="!!item.group"
        @click.prevent="selectMenu($event, item.id)"
        @keydown.enter.stop="selectMenu($event, item.id)"
        @keydown.down.prevent="focusNextItem(menuItemId(item, index))"
        @keydown.up.prevent="focusPreviousItem(menuItemId(item, index))"
        @keydown.right.prevent="
          focusNextItem(depth === 0 ? menuItemId(item, index) : null)
        "
        @keydown.left.prevent="
          focusPreviousItem(depth === 0 ? menuItemId(item, index) : null)
        "
        @keydown.esc="closeMenu($event, true)"
        @keydown.tab="closeMenu"
      >
        <span class="menu-link">{{ item.cap }}</span>

        <cat-pill
          v-if="item.relStatus"
          level="info"
          mild
          class="mlx"
          :value="item.relStatus"
        ></cat-pill>
      </a>
      <a
        v-if="item.group && depth === 0"
        :id="menuItemId(item, index)"
        class="inline-block"
        role="link"
        :tabindex="tabIndex(index)"
        aria-haspopup="true"
        :aria-expanded="active === index ? 'true' : 'false'"
        :aria-controls="groupId(item) + '_' + depth"
        :aria-label="`${item.cap}, submenu`"
        :data-label="item.cap"
        :data-menu-group="!!item.group"
        @click.prevent="toggleSubMenu(menuItemId(item, index), index, $event)"
        @keydown.enter.stop="
          toggleSubMenu(menuItemId(item, index), index, $event)
        "
        @keydown.space.prevent="
          toggleSubMenu(menuItemId(item, index), index, $event)
        "
        @keydown.down.prevent="
          toggleSubMenu(menuItemId(item, index), index, $event)
        "
        @keydown.right.prevent="
          focusNextItem(depth === 0 ? menuItemId(item, index) : null)
        "
        @keydown.left.prevent="
          focusPreviousItem(depth === 0 ? menuItemId(item, index) : null)
        "
        ><span class="menu-link">{{ item.cap }}</span></a
      >
      <transition name="sub-main-menu-hide">
        <div
          v-if="
            depth === 0 || isRoot
              ? item.text && !item.group && active === index
              : item.text && !item.group
          "
          role="group"
          class="sub-main-menu-display"
          :class="item.css"
          @click.self="focusSubMenuItem"
        >
          <h2 v-if="item.cap" role="presentation" data-label-type="heading">
            {{ item.cap }}
          </h2>
          <p class="item-text">{{ item.text }}</p>
        </div>

        <div
          v-else-if="
            depth === 0 || isRoot ? item.group && active === index : item.group
          "
          ref="submenu"
          role="group"
          :class="
            depth === 0 || isRoot
              ? 'horizontal-sub-main-menu sub-main-menu-display'
              : 'group'
          "
          @click.self="focusSubMenuItem"
        >
          <span
            v-if="item.cap"
            :id="groupId(item) + '_title_' + depth"
            role="presentation"
            :class="{
              title: true,
              'in-group': depth !== 0 && !isRoot
            }"
            @click.self="focusSubMenuItem"
            >{{ item.cap }}</span
          >
          <p
            v-if="item.text"
            class="item-text"
            role="presentation"
            @click.self="focusSubMenuItem"
          >
            {{ item.text }}
          </p>
          <cat-menu-items
            :id="groupId(item) + '_' + depth"
            :items="item.group"
            :depth="depth + 1"
            :class="depth === 0 || isRoot ? 'clearfix' : null"
            :stackCss="getStackClass(item)"
            :group="depth !== 0 && !isRoot"
            :groupLabelId="item.cap ? groupId(item) + '_title_' + depth : null"
            @ui-menu-select="selectMenu"
            @ui-menu-close="closeMenu"
          />
        </div>
      </transition>
    </li>

    <li
      v-if="depth === 0"
      ref="lightbox"
      class="cat-menu-items-bg menu-lightbox menu-lightbox-dark"
      :class="active !== null ? 'visible' : null"
      role="presentation"
      aria-hidden="true"
      @click.self="closeMenu"
    ></li>
  </ul>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import CatPill from "@/components/catPill/cat-pill";
import jQuery from "jquery";

export default defineComponent({
  name: "CatMenuItems",
  components: {
    CatPill
  },
  props: {
    items: {
      type: Array,
      default: null,
      description: "Menu item(s) displayed in drop down"
    },
    depth: {
      type: Number,
      default: 0,
      description: "If default (0), only main links will be visible."
    },
    isRoot: {
      type: Boolean,
      default: false,
      description: "If set to true this will be the main link."
    },
    group: {
      type: Boolean,
      default: false,
      description: "Groups sub menu items."
    },
    groupLabelId: {
      type: String,
      default: null,
      description: "Adds Aria label for the element."
    },
    groupLabel: {
      type: String,
      default: null,
      description: "Returns the WAI-ARIA role for the element."
    },
    stackCss: {
      type: String,
      default: "",
      description:
        "If set to 'stack-horizontally' flex: 1 1 0 will apply, if set to 'stack-vertically' flex: 1 1 auto will apply"
    }
  },

  computed: {
    ulCssClass: function () {
      var css = {
        "cat-menu-items": this.depth === 0,
        "horizontal-menu": this.depth === 0
      };
      if (this.stackCss !== "") {
        css[this.stackCss] = true;
      }
      return css;
    },
    menuRole: function () {
      let role = "";
      if (this.depth === 0) {
        role = ""; // Empty string for depth 0 (no role)
      } else if (this.group) {
        role = this.groupLabelId || this.groupLabel ? "" : "presentation";
      }
      return role === "" ? undefined : role; // Return undefined so Vue removes the attribute
    }
  },
  data: function () {
    return {
      active: null,
      tabindexValue: null
    };
  },
  methods: {
    tabIndex: function (index) {
      // Each item in the menu has tabindex set to -1,
      // except in a menubar, where the first item has tabindex set to 0
      if (this.depth === 0 || this.isRoot) {
        // No tabindex set yet, set on the first
        if (!this.tabindexValue && index === 0) {
          return 0;
        }
        // Move the tabindex 0 if submenu is previously opened
        if (this.tabindexValue === index) {
          return 0;
        }
      }
      return -1;
    },
    toggleSubMenu: function (id, index, event) {
      const parentLi = jQuery("#" + id)
        .parents("li")
        .first();
      parentLi.toggleClass("toggled");

      const isSubMenuOpen = index === this.active;
      if (isSubMenuOpen) {
        // Close the submenu
        this.active = null;
      } else {
        // Open the submenu
        this.active = index;
        this.tabindexValue = index;

        // Focus on the first sub menuitem
        // The submenu may not be a child of the menu item selected
        // Focus on the first menu item if opening from keyboard
        if (event.key) {
          this.$nextTick(function () {
            const subMenuItem = parentLi
              .find("a[role='link']")
              .not("#" + id)
              .first();
            subMenuItem.focus();
          });
        }
      }
    },
    focusSubMenuItem: function () {
      // No id passed on, focus on the first item in the submenu
      const subMenuItem = jQuery("[role='menubar'] > li.open")
        .find("a[role='link']")
        .not("[aria-haspopup='true']")
        .first();
      subMenuItem.focus();
    },
    selectMenu: function (event, id) {
      const showLoader = new CustomEvent("ui-block", { detail: {} });
      document.dispatchEvent(showLoader);
      if (id) {
        this.$emit("ui-menu-select", { value: id });
      } else if (event.value) {
        this.$emit("ui-menu-select", { value: event.value });
      }
    },
    closeMenu: function (event, focus) {
      if (this.active || (this.active === 0 && this.depth === 0)) {
        // Reached the root menu with open submenu
        if (focus || (event && event.focus)) {
          this.$nextTick(function () {
            jQuery("a[role='link'][tabindex='0']", this.$refs.theMenu)
              .first()
              .focus();
          });
        }
        this.active = null;
      } else {
        this.$emit("ui-menu-close", { focus: focus ? focus : event.focus });
      }
    },
    menuItemId: function (item, index) {
      return this.groupId(item) + "_" + index + "_" + this.depth;
    },
    groupId: function (item) {
      var cap = item.cap;
      if (cap === null || cap === undefined) {
        if (item.altTitle === null || item.altTitle === undefined) {
          return "menu_";
        }
        cap = item.altTitle;
      }
      return "menu_" + cap.replace(/\W/g, "_");
    },
    getMenuItemIndex: function (menuItems, id) {
      let index = 0;
      while (menuItems[index]) {
        if (menuItems[index].id === id) {
          return index;
        }
        index++;
      }
    },
    focusNextItem: function (id) {
      if (!id) {
        // Do nothing
        return;
      }

      // Assumption right now is we have only 1 level with respect to the menubar
      let index;
      let menuItems;
      if (this.depth === 0 || this.isRoot) {
        // Navigating the menu bar
        menuItems = jQuery("> li > a[role='link']", this.$refs.theMenu);
      } else {
        menuItems = jQuery("#" + id)
          .parents("[role='group']")
          .find("a[role='link']");
      }

      index = this.getMenuItemIndex(menuItems, id);
      if (menuItems.slice(index + 1).first().length > 0) {
        menuItems
          .slice(index + 1)
          .first()
          .focus();
      } else {
        menuItems.first().focus();
      }
    },
    focusPreviousItem: function (id) {
      if (!id) {
        // Do nothing
        return;
      }

      // Assumption right now is we have only 1 level with respect to the menubar
      let index;
      let menuItems;
      if (this.depth === 0 || this.isRoot) {
        // Navigating the menu bar
        menuItems = jQuery("> li > a[role='link']", this.$refs.theMenu);
      } else {
        menuItems = jQuery("#" + id)
          .parents("[role='group']")
          .find("a[role='link']");
      }

      index = this.getMenuItemIndex(menuItems, id);
      if (index > 0) {
        menuItems
          .slice(index - 1)
          .first()
          .focus();
      } else {
        menuItems.last().focus();
      }
    },
    getStackClass(item) {
      if (item.css === undefined || item.css === null) {
        return "";
      }

      // First menu group is always horizontal on large screen
      if (this.isGroupMenuContainer(item.group)) {
        if (this.depth === 0 || this.isRoot) {
          return "stack-horizontally-large";
        }
      } else if (item.css.includes("horizontal")) {
        return "stack-horizontally-large";
      } else if (item.css.includes("vertical")) {
        return "stack-vertically";
      }
      return "";
    },
    isGroupMenuContainer: function (items) {
      // Container if has at least 2 child menu items and one of the child is a group
      return (
        items.length >= 2 &&
        items.some(function (item) {
          return item.group && item.group.length > 0;
        })
      );
    },
    liCssClass: function (item, index) {
      var css = [];
      var itemCss = item.css ? item.css : "";
      if (this.stackCss === "stack-horizontally") {
        css.push("stack-item-nospace");
      } else if (this.stackCss === "stack-vertically") {
        css.push("stack-item-auto");
      }
      if (this.active === index) {
        css.push("open");
      }
      if (item.group) {
        css.push("has-children");
      }

      if (item && item.values && item.values.length) {
        item.values.forEach(function (value) {
          if (value.name == "displayOptions") {
            css.push(" " + value.value);
          }
        });
      }
      return css.concat(itemCss);
    },
    linkCssClass: function (item, index) {
      var css = [];
      if (item && item.values && item.values.length) {
        item.values.forEach(function (value) {
          if (value.name == "linkClass") {
            css.push(" " + value.value);
          }
        });
      }
      return css;
    },
    menuLinkClass: function (itemCss) {
      var css = [];
      var hasButtonCss = itemCss.some(function (css) {
        return ["primary", "secondary", "hollow"].includes(css);
      });
      if (hasButtonCss) {
        css.push("button");
        css.push("mbs");
      }
      return css.concat(itemCss);
    }
  },
  mounted: function () {
    // If the menu items is the root with depth greater than 0, this means it is coming from an inline menu.
    // Set the active to index 0 (zero-based)
    if (this.isRoot && this.depth > 0 && this.items.length === 1) {
      this.active = 0;
    }

    jQuery(window).scroll(function (event) {
      var scroll = jQuery(window).scrollTop();
      if (scroll > 20) {
        jQuery(".horizontal-menu > li > a").addClass("scrolled");
      } else {
        jQuery(".horizontal-menu > li > a").removeClass("scrolled");
      }
    });
  }
});
</script>
